// §4.4 Transfer DETAIL (office half).
//
// Header with the state TIMELINE reconstructed from the header principals
// (§5 row 32: created_by/at, dispatched_by/at, received_by/at,
// cancelled_by/at + cancel_reason — a separate timeline array is deliberately
// NOT served), the lines table (product · unit serial · qty · received ·
// variance reason), the §3.3 dispatch/cancel actions, and the activity log,
// which is exactly `GET /movements?source_doc_type=transfer&source_doc_id=` —
// the movement-level story the spec points at.
//
// The FLOOR receive flow is out of this slice (D-048); a dispatched transfer
// therefore shows its receive state read-only here and says where receiving
// happens.

import { enumLabel } from "../../../../lib/enum-label";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
// The register's labelled-control Field, aliased: this area's own `Field` is
// the dt/dd detail row from inventory-ui.
import { Field as FormField } from "../../../../components/field";
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  PlannedOrderRef,
  useCanSeePlannedOrigin,
} from "../../planning/components/planned-order-ref";
import {
  FacilityRef,
  Field,
  InventoryStatus,
  MoneyCell,
  MoneyColumnNote,
  RefLabel,
} from "../components/inventory-ui";
import {
  useCancelTransfer,
  useDispatchTransfer,
  useFacilities,
  useMovements,
  usePrincipalIndex,
  useProductIndex,
  useStockUnitIndex,
  useTransfer,
  type StockMovement,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { costPresence, showCostColumn } from "../lib/cost";
import { inventoryErrorMessage } from "../lib/errors";
import {
  daysPastDate,
  formatBusinessDate,
  formatMeasure,
  formatTimestamp,
  isOverdue,
  formatMeasureWithUom,
} from "../lib/measure";

export function TransferDetailScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const canSeeOrigin = useCanSeePlannedOrigin();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const detail = useTransfer(id);
  const dispatch = useDispatchTransfer();
  const cancel = useCancelTransfer();
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  const facilities = useFacilities();
  const facilityName = useMemo(() => {
    const map = new Map<string, string>();
    for (const facility of facilities.data?.items ?? []) map.set(facility.id, facility.name);
    return map;
  }, [facilities.data]);

  const lines = detail.data?.lines ?? [];
  const products = useProductIndex(
    lines.flatMap((line) => (line.product_id !== null ? [line.product_id] : [])),
  );
  const units = useStockUnitIndex(
    lines.flatMap((line) => (line.stock_unit_id !== null ? [line.stock_unit_id] : [])),
  );

  const principals = usePrincipalIndex(perms.canReadMembers);

  const activity = useMovements(
    id.length > 0 ? { source_doc_type: "transfer", source_doc_id: id, limit: 100 } : {},
  );

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  if (detail.isPending || detail.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("inventory.transferDetail.loading")} />;
  }
  if (detail.error) {
    return (
      <ErrorState
        title={t("inventory.transferDetail.errorTitle")}
        detail={detail.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void detail.refetch()}
        requestId={requestIdOf(detail.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const transfer = detail.data.transfer;
  const inFlight = transfer.status === "dispatched" || transfer.status === "partially_received";
  const overdue = inFlight && isOverdue(transfer.eta_on);
  const canDispatch = perms.has("inventory.transfer.manage") && transfer.status === "draft";
  const canCancel =
    perms.has("inventory.transfer.manage") &&
    (transfer.status === "draft" || transfer.status === "dispatched");

  const movements = activity.data?.items ?? [];
  const presence = costPresence(movements, (row) => row.unit_cost_minor);
  const withCost = showCostColumn(presence);

  return (
    <section
      className="transfer-detail"
      data-testid="transfer-detail"
      data-code={transfer.transfer_code}
      data-status={transfer.status}
    >
      <PageHeader
        crumbs={[{ label: term("transfer"), to: "/inventory/transfers" }]}
        title={transfer.transfer_code}
        actions={
          <>
            <InventoryStatus status={transfer.status} scope="transfer" />
          </>
        }
      />

      {transfer.planned_order_id !== null && canSeeOrigin ? (
        // Spec 06 §3.3 (D-389 ruling 5, D-396 ruling 23): a draft converted
        // from a planned order says which, linked to module 03's drawer for a
        // reader of planned orders; nothing for anyone else.
        <p className="transfer-origin" data-testid="transfer-origin">
          <PlannedOrderRef
            plannedOrderId={transfer.planned_order_id}
            testId="transfer-planned-order"
            facilityId={transfer.to_facility_id}
            kind="transfer"
          />
        </p>
      ) : null}

      {overdue ? (
        <p className="degraded-note is-warning" role="note">
          {t("inventory.transfers.daysLate", { n: daysPastDate(transfer.eta_on) })}
        </p>
      ) : null}

      <ol className="state-timeline" aria-label={t("inventory.transferDetail.timeline")}>
        <TimelineStep
          label={t("inventory.transferDetail.created")}
          at={transfer.created_at}
          by={transfer.created_by}
          nameOf={(who) => principals.byId.get(who)}
          pending={principals.isPending}
          done
        />
        <TimelineStep
          label={t("inventory.transferDetail.dispatched")}
          at={transfer.dispatched_at}
          by={transfer.dispatched_by}
          nameOf={(who) => principals.byId.get(who)}
          pending={principals.isPending}
          done={transfer.dispatched_at !== null}
        />
        <TimelineStep
          label={t("inventory.transferDetail.received")}
          at={transfer.received_at}
          by={transfer.received_by}
          nameOf={(who) => principals.byId.get(who)}
          pending={principals.isPending}
          done={transfer.received_at !== null}
        />
        {transfer.cancelled_at !== null ? (
          <TimelineStep
            label={t("inventory.transferDetail.cancelled")}
            at={transfer.cancelled_at}
            by={transfer.cancelled_by}
            nameOf={(who) => principals.byId.get(who)}
            pending={principals.isPending}
            done
          />
        ) : null}
      </ol>

      <dl className="inv-fields">
        <Field label={t("inventory.transfers.colRoute")}>
          <FacilityRef
            id={transfer.from_facility_id}
            name={facilityName.get(transfer.from_facility_id)}
            pending={facilities.isPending}
          />
          {" → "}
          <FacilityRef
            id={transfer.to_facility_id}
            name={facilityName.get(transfer.to_facility_id)}
            pending={facilities.isPending}
          />
        </Field>
        <Field label={t("inventory.transfers.colEta")}>
          {transfer.eta_on === null
            ? t("inventory.transfers.noEta")
            : formatBusinessDate(transfer.eta_on, i18n.language)}
        </Field>
        <Field label={t("inventory.transferDetail.carrier")}>
          {transfer.carrier ?? t("inventory.common.none")}
        </Field>
        {transfer.cancel_reason !== null ? (
          <Field label={t("inventory.transferDetail.cancelReason")}>{transfer.cancel_reason}</Field>
        ) : null}
      </dl>

      <div className="unit-actions" role="group" aria-label={t("inventory.transferDetail.actions")}>
        {canDispatch ? (
          <Button
            variant="primary"
            data-testid="transfer-dispatch"
            busy={dispatch.isPending}
            busyLabel={t("inventory.transferDetail.dispatching")}
            onClick={() => {
              dispatch.mutate({ id });
            }}
          >
            {t("inventory.transferDetail.dispatch")}
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            onClick={() => {
              setCancelOpen((prev) => !prev);
            }}
          >
            {t("inventory.transferDetail.cancel")}
          </Button>
        ) : null}
      </div>
      {dispatch.isError ? (
        <p className="form-error" role="alert">
          {inventoryErrorMessage(t, dispatch.error, "inventory.transferDetail.dispatchFailed")}
        </p>
      ) : null}

      {cancel.isSuccess ? (
        <p className="inv-success" role="status">
          {t("inventory.transferDetail.cancelDone")}
        </p>
      ) : null}

      {cancelOpen && !cancel.isSuccess ? (
        <form
          className="inv-panel"
          onSubmit={(event) => {
            event.preventDefault();
            // The panel closes itself on success: leaving it open with a live
            // Confirm — while the toggle that would close it disappears with
            // `canCancel` — guaranteed a second click into TRANSFER_STATE_INVALID.
            cancel.mutate(
              { id, reason: cancelReason.trim() },
              {
                onSuccess: () => {
                  setCancelOpen(false);
                },
              },
            );
          }}
        >
          <FormField label={t("inventory.transferDetail.cancelReason")}>
            <textarea
              value={cancelReason}
              onChange={(event) => {
                setCancelReason(event.target.value);
              }}
              required
            />
          </FormField>
          {cancel.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, cancel.error, "inventory.transferDetail.cancelFailed")}
            </p>
          ) : null}
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              busy={cancel.isPending}
              disabled={cancelReason.trim().length === 0}
            >
              {t("inventory.transferDetail.confirmCancel")}
            </Button>
          </div>
        </form>
      ) : null}

      <h2>{t("inventory.transferDetail.lines")}</h2>
      {/* A draft with no lines is a real state (§3.3 lets one be created and
          edited), and the §4 preamble wants an empty state for every list. */}
      {lines.length === 0 ? (
        <EmptyState
          title={t("inventory.transferDetail.noLinesTitle")}
          body={t("inventory.transferDetail.noLinesBody")}
        />
      ) : (
        <DataTable
          columns={[
            { key: "line_no", header: "#", id: true },
            {
              key: "product_id",
              header: t("inventory.transferDetail.colProduct"),
              render: (line) =>
                line.product_id === null ? (
                  "—"
                ) : (
                  <RefLabel
                    value={products.byId.get(line.product_id)?.sku}
                    id={line.product_id}
                    pending={products.isPending}
                    kind="product"
                  />
                ),
            },
            {
              key: "stock_unit_id",
              header: term("stock_unit"),
              render: (line) =>
                line.stock_unit_id === null ? (
                  "—"
                ) : (
                  <Link
                    to="/inventory/units/$id"
                    params={{ id: line.stock_unit_id }}
                    className="ds-lnk"
                  >
                    <RefLabel
                      value={units.byId.get(line.stock_unit_id)?.serial_code}
                      id={line.stock_unit_id}
                      pending={units.isPending}
                      kind="stock_unit"
                    />
                  </Link>
                ),
            },
            {
              key: "quantity",
              header: t("inventory.transferDetail.colQty"),
              numeric: true,
              render: (line) =>
                formatMeasureWithUom(line.quantity, line.uom, { locale: i18n.language }),
            },
            {
              key: "received_qty_base",
              header: t("inventory.transferDetail.colReceived"),
              numeric: true,
              render: (line) => formatMeasure(line.received_qty_base, { locale: i18n.language }),
            },
            {
              key: "receive_variance_reason",
              header: t("inventory.transferDetail.colVariance"),
              render: (line) => line.receive_variance_reason ?? "—",
            },
          ]}
          rows={lines}
          rowKey={(line) => line.id}
          empty={null}
          rowTestId="transfer-line"
          rowProps={(line) => ({
            "data-qty": line.qty_base,
            "data-received": line.received_qty_base,
            "data-variance-reason": line.receive_variance_reason ?? "",
          })}
        />
      )}

      {inFlight ? <p className="inv-hint">{t("inventory.transferDetail.receiveOnFloor")}</p> : null}

      <h2>{t("inventory.transferDetail.activity")}</h2>
      {activity.isPending ? (
        <TableSkeleton rows={3} cols={4} label={t("inventory.transferDetail.activityLoading")} />
      ) : activity.error ? (
        <ErrorState
          title={t("inventory.transferDetail.activityError")}
          detail={activity.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void activity.refetch()}
          requestId={requestIdOf(activity.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : movements.length === 0 ? (
        <p className="inv-hint">{t("inventory.transferDetail.activityEmpty")}</p>
      ) : (
        <>
          <MoneyColumnNote presence={presence} />
          <DataTable
            columns={[
              {
                key: "moved_at",
                header: t("inventory.unitCard.colWhen"),
                id: true,
                render: (movement) => formatTimestamp(movement.moved_at, i18n.language),
              },
              {
                key: "movement_type",
                header: t("inventory.unitCard.colEvent"),
                render: (movement) =>
                  enumLabel(t, "inventory.vocab.movement", movement.movement_type),
              },
              {
                key: "quantity",
                header: t("inventory.unitCard.colQuantity"),
                numeric: true,
                render: (movement) =>
                  formatMeasureWithUom(movement.quantity, movement.uom, { locale: i18n.language }),
              },
              // §7 redaction-by-absence.
              ...(withCost
                ? [
                    {
                      key: "unit_cost_minor",
                      header: t("inventory.money.unitCost"),
                      numeric: true,
                      render: (movement: StockMovement) => (
                        <MoneyCell
                          row={movement}
                          valueOf={(row) => row.unit_cost_minor}
                          currencyOf={(row) => row.currency}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            rows={movements}
            rowKey={(movement) => movement.id}
            empty={null}
          />
        </>
      )}
    </section>
  );
}

function TimelineStep({
  label,
  at,
  by,
  nameOf,
  pending,
  done,
}: {
  label: string;
  at: string | null;
  by: string | null;
  nameOf: (who: string) => string | undefined;
  pending: boolean;
  done: boolean;
}) {
  const { t, i18n } = useTranslation();
  return (
    <li className={`timeline-step${done ? " is-done" : ""}`}>
      <span className="timeline-label">{label}</span>
      <span className="timeline-when">
        {at === null ? t("inventory.transferDetail.pending") : formatTimestamp(at, i18n.language)}
      </span>
      {by !== null ? (
        <span className="timeline-who">
          {t("inventory.transferDetail.by", { who: "" })}{" "}
          <RefLabel value={nameOf(by)} id={by} pending={pending} kind="user" />
        </span>
      ) : null}
    </li>
  );
}
